/**
 * Pricing Page
 *
 * Displays subscription plans dynamically loaded from Supabase database.
 * Plans automatically update when database changes.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchSubscriptionPlans,
  calculateAnnualSavings,
  type SubscriptionPlan,
} from '@/services/subscriptionService';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { PlanCard } from '@/features/subscription/components/PlanCard';
import { isStripeRedirectUrl } from '../../lib/stripeRedirect';

type BillingCycle = 'monthly' | 'yearly';

const PricingPage = () => {
  const { t, i18n } = useTranslation('pricing');
  const { subscription: userSubscription, session, hasPaidSubscription } = useAuth();

  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [apiOnlyEligible, setApiOnlyEligible] = useState(false);

  // FIX-6: shown when BE returns 409 SUBSCRIPTION_ALREADY_ACTIVE, or when
  // the FE-side guard catches a duplicate-subscription attempt before the
  // round-trip. Both signals are required: client check for UX, server
  // check for security.
  const [duplicateSubModal, setDuplicateSubModal] = useState<{
    open: boolean;
    currentPlanName: string | null;
  }>({ open: false, currentPlanName: null });

  // Load plans from database (only those flagged visible on pricing and not deprecated)
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchSubscriptionPlans();
        const visible = data
          .filter((p) => p.is_visible_on_pricing && !p.is_deprecated)
          .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
        setPlans(visible);
      } catch (err) {
        setError(t('pricing.error.loadFailed', 'Failed to load pricing plans'));
        console.error('Failed to load pricing plans:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, [t]);

  // #131: API-Only is selectable only when the user has a trained model.
  // The backend is the source of truth; a failed fetch fails safe (locked),
  // and the checkout endpoint still enforces it server-side.
  useEffect(() => {
    const token = session?.access_token;
    if (!token) {
      setApiOnlyEligible(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/stripe/api-only-eligibility`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) {
          if (!cancelled) setApiOnlyEligible(false);
          return;
        }
        const data = await res.json();
        if (!cancelled) setApiOnlyEligible(Boolean(data?.eligible));
      } catch {
        if (!cancelled) setApiOnlyEligible(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.access_token]);

  const getPlanPrice = (plan: SubscriptionPlan): number => {
    if (billingCycle === 'yearly' && plan.price_yearly) {
      return plan.price_yearly;
    }
    return plan.price_monthly;
  };

  const getSavings = (plan: SubscriptionPlan): number | null => {
    if (billingCycle === 'yearly' && plan.price_yearly && plan.price_monthly > 0) {
      return calculateAnnualSavings(plan.price_monthly, plan.price_yearly);
    }
    return null;
  };

  const isCurrentPlan = (planName: string): boolean => {
    return userSubscription?.plan_name?.toLowerCase() === planName.toLowerCase();
  };

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (isCurrentPlan(plan.name)) {
      return; // Already on this plan
    }

    // Unauthenticated users → route to registration with plan pre-selected
    if (!session?.access_token) {
      const target = plan.slug
        ? `/register?plan=${encodeURIComponent(plan.slug)}`
        : '/register';
      window.location.href = target;
      return;
    }

    // FIX-6 (client-side UX guard): if we know locally that the user is
    // already on a paid plan, surface the modal immediately instead of
    // round-tripping to the BE just to get a 409 back. The BE check is
    // still the security boundary (clients can lie about subscription
    // state), but this saves a round-trip on the happy path.
    if (hasPaidSubscription && userSubscription?.plan_name) {
      setDuplicateSubModal({
        open: true,
        currentPlanName: userSubscription.plan_name,
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // For paid plans, call backend to create checkout session
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          plan_id: plan.id,
          billing_cycle: billingCycle,
          // #127: open Stripe Checkout in the app's current language so it
          // doesn't fall back to Stripe's auto-detected (often German) page.
          locale: i18n.language?.slice(0, 2) || 'en'
        })
      });

      // FIX-6: BE now returns 409 SUBSCRIPTION_ALREADY_ACTIVE if the user
      // has an existing active/trialing subscription. Surface the modal
      // so the user can route to the customer portal instead of seeing
      // a generic error toast.
      if (response.status === 409) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData?.code === 'SUBSCRIPTION_ALREADY_ACTIVE') {
          setDuplicateSubModal({
            open: true,
            currentPlanName: userSubscription?.plan_name ?? null,
          });
          setLoading(false);
          return;
        }
      }

      // #131: BE returns 403 ELIGIBILITY_NOT_MET when an ineligible user
      // bypasses the locked card and posts directly. Surface the same
      // trained-model message instead of a generic checkout error.
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData?.code === 'ELIGIBILITY_NOT_MET') {
          setError(t('pricing.apiOnlyLocked', 'Available after you train at least one model.'));
          setLoading(false);
          return;
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      if (!isStripeRedirectUrl(url)) {
        throw new Error('Refusing to redirect to a non-Stripe URL');
      }
      // Redirect to Stripe checkout
      window.location.href = url;

    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(t('pricing.error.checkoutFailed', 'Failed to start checkout process. Please try again.'));
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check authentication
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Call backend to create customer portal session
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/stripe/customer-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        // #127: open the Stripe billing portal in the app's current language
        // instead of Stripe's auto-detected (often German) locale.
        body: JSON.stringify({ locale: i18n.language?.slice(0, 2) || 'en' })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open customer portal');
      }

      const { url } = await response.json();

      if (!isStripeRedirectUrl(url)) {
        throw new Error('Refusing to redirect to a non-Stripe URL');
      }
      // Redirect to Stripe customer portal
      window.location.href = url;

    } catch (err) {
      console.error('Error opening customer portal:', err);
      setError(t('pricing.error.portalFailed', 'Failed to open subscription management. Please try again.'));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('pricing.loading', 'Loading plans...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-foreground font-semibold">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            {t('pricing.error.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground sm:text-5xl mb-4">
            {t('pricing.title', 'Choose Your Plan')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t(
              'pricing.subtitle',
              'Select the perfect plan for your data processing needs. Upgrade or downgrade anytime.'
            )}
          </p>

          {/* Manage Subscription Button (only for paid users) */}
          {hasPaidSubscription && (
            <div className="mt-6">
              <button
                onClick={handleManageSubscription}
                className="inline-flex items-center gap-2 px-6 py-3 bg-card text-foreground rounded-lg font-medium shadow-md hover:shadow-lg hover:bg-muted transition-all border border-border"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t('pricing.manageSubscription', 'Manage Subscription')}
              </button>
              <p className="text-sm text-muted-foreground mt-2">
                {t('pricing.manageHint', 'Cancel anytime, update payment method, or view invoices')}
              </p>
            </div>
          )}

          {/* Billing Cycle Toggle */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-foreground hover:bg-muted'
              }`}
            >
              {t('pricing.monthly', 'Monthly')}
            </button>
            <div className="relative">
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  billingCycle === 'yearly'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-foreground hover:bg-muted'
                }`}
              >
                {t('pricing.yearly', 'Yearly')}
              </button>
              <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                {t('pricing.save', 'Save up to 17%')}
              </span>
            </div>
          </div>
        </div>

        {/* Plans Grid — renders via shared PlanCard component */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-12 items-stretch">
          {plans.map((plan) => {
            const isUpgradeOnly = plan.is_upgrade_only || plan.cta_type === 'upgrade_only';
            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                variant="pricing"
                price={getPlanPrice(plan)}
                pricePeriodLabel={billingCycle === 'monthly'
                  ? (t('pricing.perMonth', 'month') as string)
                  : (t('pricing.perYear', 'year') as string)}
                savingsPercent={getSavings(plan)}
                isCurrent={isCurrentPlan(plan.name)}
                upgradeOnlyLocked={isUpgradeOnly && !apiOnlyEligible}
                onSelect={handleSelectPlan}
              />
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <div className="text-center text-muted-foreground mt-12">
          <p>
            {t('pricing.questions', 'Questions?')}{' '}
            <a href="mailto:info@forecast-engine.com" className="text-primary hover:underline">
              {t('pricing.contactUs', 'Contact us')}
            </a>
          </p>
        </div>
      </div>

      {/* FIX-6: duplicate-subscription modal. Triggered when BE returns
          409 SUBSCRIPTION_ALREADY_ACTIVE OR when the local FE check
          short-circuits the request. */}
      {duplicateSubModal.open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="duplicate-sub-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
        >
          <div className="bg-card rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h3
              id="duplicate-sub-title"
              className="text-xl font-bold text-foreground mb-4"
            >
              {t(
                'pricing.duplicateSub.title',
                'You already have an active subscription'
              )}
            </h3>
            <p className="text-muted-foreground mb-6">
              {duplicateSubModal.currentPlanName
                ? t(
                    'pricing.duplicateSub.messageWithPlan',
                    "You're already on {{plan}}. To change plans, manage your subscription via the billing portal.",
                    { plan: duplicateSubModal.currentPlanName }
                  )
                : t(
                    'pricing.duplicateSub.message',
                    'To change plans, manage your subscription via the billing portal.'
                  )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() =>
                  setDuplicateSubModal({ open: false, currentPlanName: null })
                }
                className="flex-1 bg-muted text-foreground py-3 px-6 rounded-lg font-semibold hover:bg-muted/80 transition-colors"
              >
                {t('pricing.duplicateSub.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => {
                  setDuplicateSubModal({ open: false, currentPlanName: null });
                  handleManageSubscription();
                }}
                className="flex-1 bg-primary text-primary-foreground py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                {t('pricing.duplicateSub.manage', 'Manage subscription')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
